import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuthLite } from "@/context/AuthLite";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { Mail, Lock } from "lucide-react";
import { apiUrl, apiFetch } from "@/lib/api-client";
import { cn } from "@/lib/utils";

const PRIVACY_POLICY_URL = "https://www.tptbrany.sk/gdpr/";

function LoginLegalFooter({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <p className={cn("text-center text-xs text-muted-foreground/60 leading-relaxed", className)}>
      <span>{t("auth.copyright")}</span>
      <span className="mx-1.5 text-muted-foreground/35 select-none" aria-hidden="true">
        ·
      </span>
      <a
        href={PRIVACY_POLICY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-2 decoration-muted-foreground/30 hover:text-muted-foreground/90 hover:decoration-muted-foreground/50 focus:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm"
      >
        {t("auth.privacyPolicy")}
      </a>
    </p>
  );
}

export default function Login() {
  const { t } = useTranslation();
  const { user, isLoading, authMode, refreshUser } = useAuthLite();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user && !isLoading) {
      navigate("/crm", { replace: true });
    }
  }, [user, isLoading, navigate]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await apiFetch("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password, remember }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError((data as { message?: string }).message ?? t("auth.errorLogin"));
        return;
      }
      // Audit C-12 / H-18: backend no longer returns the JWT in the response
      // body; auth is carried exclusively by the httpOnly `auth` cookie.
      // Any legacy localStorage/sessionStorage copy is proactively removed.
      try {
        localStorage.removeItem("email_jwt");
        sessionStorage.removeItem("email_jwt");
      } catch {}
      const me = await refreshUser();
      if (!me) {
        setError(t("auth.errorSessionCookie"));
        return;
      }
      sessionStorage.setItem("loginTransition", "1");
      navigate("/crm", { replace: true });
    } catch {
      setError(t("auth.errorGeneric"));
    } finally {
      setSubmitting(false);
    }
  }

  if (authMode === "none") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white dark:bg-card rounded-2xl shadow-2xl p-8 md:p-10">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-foreground mb-2">{t("auth.login")}</h1>
              <p className="text-muted-foreground">{t("auth.appName")}</p>
            </div>
            <div className="space-y-4">
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  <strong>{t("auth.demoMode")}</strong> {t("auth.demoModeDesc")}
                </p>
              </div>
              <button
                onClick={() => navigate("/crm")}
                className="w-full px-4 py-3 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors"
              >
                {t("auth.enterApp")}
              </button>
            </div>
            <p className="mt-8 pt-8 border-t border-border text-center text-sm text-muted-foreground">
              {t("auth.demoNoAuth")}
            </p>
          </div>
          <LoginLegalFooter className="mt-8" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left: branded panel – logo top-left 2× size, text centered + italic */}
      <div
        className={cn(
          "hidden md:flex md:w-[40%] flex-col relative",
          "bg-sidebar text-sidebar-foreground"
        )}
      >
        <img
          src="/tpt-brany-logo.png"
          alt="TPT Brány"
          className="absolute left-6 top-6 lg:left-8 lg:top-8 h-16 w-auto object-contain z-10"
        />
        <div className="flex-1 flex items-center justify-center px-8 lg:px-12">
          <p className="text-lg leading-relaxed text-white/90 max-w-md text-center italic">
            {t("auth.brandText")}
          </p>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex flex-col justify-center bg-background px-6 py-12 md:px-12 lg:px-20">
        <div className="w-full max-w-md mx-auto">
          <div className="md:hidden mb-8">
            <img
              src="/tpt-brany-logo.png"
              alt="TPT Brány"
              className="h-8 w-auto object-contain"
            />
            <p className="text-sm text-muted-foreground mt-2">{t("auth.brandText")}</p>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-1">{t("auth.login")}</h1>
          <p className="text-muted-foreground mb-8">{t("auth.loginSubtitle")}</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label htmlFor="login-email" className="block text-sm font-medium text-foreground mb-1">
                {t("auth.email")}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("auth.placeholderEmail")}
                  required
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>
            <div>
              <label htmlFor="login-password" className="block text-sm font-medium text-foreground mb-1">
                {t("auth.password")}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  id="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("auth.placeholderPassword")}
                  required
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>
            <div className="flex items-center">
              <input
                id="login-remember"
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="rounded border-input accent-primary"
              />
              <label htmlFor="login-remember" className="ml-2 text-sm text-foreground">
                {t("auth.rememberMe")}
              </label>
            </div>
            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-muted-foreground hover:text-foreground hover:underline"
              >
                {t("auth.forgotPassword")}
              </Link>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              className="w-full px-4 py-3 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {submitting ? t("auth.loginSubmitting") : t("auth.loginButton")}
            </button>
          </form>

          <LoginLegalFooter className="mt-12" />
        </div>
      </div>
    </div>
  );
}
